import { Link, useNavigate } from "react-router-dom";
import { Search, ShoppingCart, User, LogOut, Shield, Heart, GitCompare, MapPin, Truck, HelpCircle, ChevronDown, Menu, X as XIcon, Bell } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { useFavorites, useCompare } from "../context/FavoritesCompareContext";
import { useI18n, LANGS } from "../context/I18nContext";
import { useCurrency, CURRENCIES } from "../context/CurrencyContext";
import { useState, useEffect } from "react";
import { api } from "../lib/api";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";

const CATEGORY_LINKS = [
  { slug: "telephones", name: "Téléphones" },
  { slug: "audio", name: "Audio & Casques" },
  { slug: "electromenager", name: "Électroménager" },
  { slug: "energie-solaire", name: "Énergie solaire" },
  { slug: "batteries", name: "Batteries" },
];

const NAV_LINKS = [
  { to: "/", key: "home" },
  { to: "/products", key: "shop" },
  { to: "/products?category=telephones", key: "categories" },
  { to: "/products?category=meilleures-ventes", key: "products" },
  { to: "/help", key: "help" },
  { to: "/contact", key: "contact" },
];

export default function Header() {
  const { user, logout } = useAuth();
  const { totals } = useCart();
  const fav = useFavorites();
  const cmp = useCompare();
  const { lang, setLang, t } = useI18n();
  const { currency, setCurrency, format } = useCurrency();
  const [q, setQ] = useState("");
  const [openUser, setOpenUser] = useState(false);
  const [openCats, setOpenCats] = useState(false);
  const [openLang, setOpenLang] = useState(false);
  const [openCurrency, setOpenCurrency] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  const submit = (e) => {
    e.preventDefault();
    if (q.trim()) navigate(`/products?search=${encodeURIComponent(q.trim())}`);
  };

  const IconBox = ({ to, icon: Icon, label, sub, count, testid }) => (
    <Link to={to} className="hidden md:flex items-center gap-2 text-gray-700 hover:text-[#0052CC] transition px-1" data-testid={testid}>
      <div className="relative">
        <Icon className="w-6 h-6" />
        {count > 0 && <span className="absolute -top-1.5 -right-1.5 bg-[#E3000F] text-white text-[10px] font-bold rounded-full min-w-[16px] h-[16px] px-1 flex items-center justify-center">{count}</span>}
      </div>
      <div className="hidden lg:block leading-tight">
        <p className="text-[10px] text-gray-500">{sub}</p>
        <p className="text-xs font-semibold">{label}</p>
      </div>
    </Link>
  );

  return (
    <header className="sticky top-0 z-40 shadow-sm je-glass-sticky-header" data-testid="site-header">
      {/* Top strip dark — responsive: promo left, quick links (Langue / Aide / Suivi) always visible */}
      <div className="bg-[#0a1430] text-white text-[11px] sm:text-xs">
        <div className="je-container flex items-center justify-between gap-2 py-2">
          {/* Left promo (truncates on small screens) */}
          <div className="text-white/80 truncate min-w-0 flex-1">
            <span className="hidden sm:inline">{t("freeShipping")} • {t("trustedSupport")}</span>
            <span className="sm:hidden">{t("freeShipping")}</span>
          </div>

          {/* Right quick actions — always visible */}
          <div className="flex items-center gap-1.5 sm:gap-3 md:gap-4 shrink-0" data-testid="header-top-actions">
            {/* Track order */}
            <Link
              to="/track"
              className="text-white/80 hover:text-white inline-flex items-center gap-1 whitespace-nowrap"
              data-testid="top-track-link"
              aria-label={t("trackingOrder")}
            >
              <Truck className="w-3.5 h-3.5 shrink-0" />
              <span className="hidden sm:inline">{t("trackingOrder")}</span>
              <span className="sm:hidden">Suivi</span>
            </Link>

            {/* Help / FAQ */}
            <Link
              to="/help"
              className="text-white/80 hover:text-white inline-flex items-center gap-1 whitespace-nowrap"
              data-testid="top-help-link"
              aria-label={t("faq")}
            >
              <HelpCircle className="w-3.5 h-3.5 shrink-0" />
              <span className="hidden sm:inline">{t("faq")}</span>
              <span className="sm:hidden">Aide</span>
            </Link>

            {/* Locate store — desktop only (secondary link) */}
            <Link to="/contact" className="hidden md:inline-flex hover:text-white text-white/80 items-center gap-1 whitespace-nowrap">
              <MapPin className="w-3.5 h-3.5" /> {t("locateStore")}
            </Link>

            {/* Language selector — compact on mobile (flag only), full on desktop */}
            <div className="relative">
              <button
                onClick={() => setOpenLang(v => !v)}
                className="text-white/80 hover:text-white inline-flex items-center gap-1 whitespace-nowrap"
                data-testid="lang-toggle"
                aria-label="Changer la langue"
              >
                <span className="text-sm leading-none">{LANGS.find(l => l.code === lang)?.flag}</span>
                <span className="hidden sm:inline">{LANGS.find(l => l.code === lang)?.label}</span>
                <ChevronDown className="w-3 h-3" />
              </button>
              {openLang && (
                <div className="absolute right-0 top-full mt-1 bg-white shadow-xl border rounded-lg py-1 z-50 min-w-[140px]">
                  {LANGS.map((l) => (
                    <button
                      key={l.code}
                      onClick={() => { setLang(l.code); setOpenLang(false); }}
                      className={`w-full text-left px-3 py-1.5 text-gray-700 hover:bg-gray-50 flex items-center gap-2 ${l.code === lang ? "font-bold text-[#0052CC]" : ""}`}
                      data-testid={`lang-${l.code}`}
                    >
                      <span>{l.flag}</span> {l.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Currency selector — visible on all screens */}
            <div className="relative">
              <button
                onClick={() => setOpenCurrency(v => !v)}
                className="text-white/80 hover:text-white inline-flex items-center gap-1 whitespace-nowrap"
                data-testid="currency-toggle"
                aria-label="Changer la devise"
              >
                <span className="font-semibold">{currency}</span>
                <ChevronDown className="w-3 h-3" />
              </button>
              {openCurrency && (
                <div className="absolute right-0 top-full mt-1 bg-white shadow-xl border rounded-lg py-1 z-50 min-w-[120px]">
                  {CURRENCIES.map((c) => (
                    <button
                      key={c.code}
                      onClick={() => { setCurrency(c.code); setOpenCurrency(false); }}
                      className={`w-full text-left px-3 py-1.5 text-gray-700 hover:bg-gray-50 flex items-center gap-2 ${c.code === currency ? "font-bold text-[#0052CC]" : ""}`}
                      data-testid={`currency-${c.code}`}
                    >
                      <span className="text-xs w-4 text-center">{c.symbol}</span> {c.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main row: logo + toutes categories dropdown + search + icons */}
      <div className="bg-white border-b border-gray-100">
        <div className="je-container flex items-center gap-3 md:gap-4 py-3 md:py-4">
          <Link to="/" className="flex items-center shrink-0" data-testid="logo-link" aria-label="John Elektwo">
            <img src="/assets/logo-icon.png" alt="John Elektwo" className="h-10 w-10 object-contain md:hidden" data-testid="logo-icon" />
            <img src="/assets/logo.png" alt="John Elektwo" className="hidden md:block h-10 lg:h-12 w-auto object-contain" data-testid="logo-full" />
          </Link>

          <form onSubmit={submit} className="flex-1 max-w-3xl min-w-0">
            <div className="flex items-stretch bg-white border-2 border-[#0052CC] rounded-lg overflow-hidden">
              <div className="hidden md:block relative">
                <button type="button" onClick={() => setOpenCats(v => !v)} className="h-full flex items-center gap-2 px-4 text-sm font-semibold text-gray-700 border-r border-gray-200 whitespace-nowrap" data-testid="cats-dropdown">
                  {t("allCategoriesDropdown")} <ChevronDown className={`w-4 h-4 transition ${openCats ? "rotate-180" : ""}`} />
                </button>
                {openCats && (
                  <div className="absolute top-full left-0 mt-1 bg-white shadow-xl border rounded-lg min-w-[220px] z-50">
                    {CATEGORY_LINKS.map((c) => (
                      <Link key={c.slug} to={`/products?category=${c.slug}`} onClick={() => setOpenCats(false)} className="block px-4 py-2 text-sm hover:bg-gray-50 hover:text-[#0052CC]">{c.name}</Link>
                    ))}
                  </div>
                )}
              </div>
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t("search")}
                className="flex-1 px-4 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 outline-none"
                data-testid="search-input"
              />
              <button type="submit" className="bg-[#0052CC] hover:bg-[#003D99] px-6 flex items-center gap-2 text-white text-sm font-semibold transition-colors" data-testid="search-btn">
                <Search className="w-4 h-4" /> <span className="hidden sm:inline">{t("searchBtn")}</span>
              </button>
            </div>
          </form>

          <div className="flex items-center gap-2 md:gap-4">
            <DropdownMenu open={openUser} onOpenChange={setOpenUser}>
              <DropdownMenuTrigger className="flex items-center gap-2 text-gray-700 hover:text-[#0052CC]" data-testid="user-menu-trigger">
                <User className="w-6 h-6" />
                <div className="hidden lg:block leading-tight text-left">
                  <p className="text-[10px] text-gray-500">{t("myAccount")}</p>
                  <p className="text-xs font-semibold">{user ? user.name?.split(" ")[0] : t("login")}</p>
                </div>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                {user ? (
                  <>
                    <div className="px-3 py-2"><p className="text-sm font-semibold">{user.name}</p><p className="text-xs text-gray-500 truncate">{user.email}</p></div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate("/account")} data-testid="account-menu-item"><User className="w-4 h-4 mr-2" /> Mon compte</DropdownMenuItem>
                    {user.role === "admin" && <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="admin-menu-item"><Shield className="w-4 h-4 mr-2" /> Admin</DropdownMenuItem>}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={logout} data-testid="logout-menu-item"><LogOut className="w-4 h-4 mr-2" /> Déconnexion</DropdownMenuItem>
                  </>
                ) : (
                  <>
                    <DropdownMenuItem onClick={() => navigate("/login")} data-testid="login-menu-item">Se connecter</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/register")} data-testid="register-menu-item">Créer un compte</DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            <IconBox to="/favorites" icon={Heart} sub={t("favorites")} label={t("favorites")} count={fav.count} testid="favorites-link" />
            <IconBox to="/compare" icon={GitCompare} sub={t("compare")} label={t("compare")} count={cmp.count} testid="compare-link" />
            {user && <NotifBell />}
            <Link to="/cart" className="flex items-center gap-2 text-gray-700 hover:text-[#0052CC] transition" data-testid="cart-link">
              <div className="relative">
                <ShoppingCart className="w-6 h-6" />
                {totals.count > 0 && <span className="absolute -top-1.5 -right-1.5 bg-[#E3000F] text-white text-[10px] font-bold rounded-full min-w-[16px] h-[16px] px-1 flex items-center justify-center" data-testid="cart-count">{totals.count}</span>}
              </div>
              <div className="hidden lg:block leading-tight">
                <p className="text-[10px] text-gray-500">{t("cart")}</p>
                <p className="text-xs font-semibold">{format(totals.subtotal)}</p>
              </div>
            </Link>
          </div>
        </div>
      </div>

      {/* Third row: All categories button + nav + right message */}
      <div className="bg-white border-b border-gray-200">
        <div className="je-container flex items-center gap-6 py-3">
          <button onClick={() => setMobileOpen(v => !v)} className="bg-[#0052CC] text-white text-sm font-semibold px-4 py-2 rounded-md flex items-center gap-2 shrink-0" data-testid="all-cats-btn">
            {mobileOpen ? <XIcon className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
            <span className="hidden sm:inline">{t("allCategories")}</span>
          </button>
          <nav className="hidden md:flex items-center gap-6 text-sm font-semibold flex-1">
            {NAV_LINKS.map((l, i) => (
              <Link key={l.to+i} to={l.to} className={`hover:text-[#0052CC] ${i === 0 ? "text-[#E3000F]" : "text-gray-800"}`}>{t(l.key)}</Link>
            ))}
            <Link to="/wheel" className="ml-auto inline-flex items-center gap-1.5 bg-gradient-to-r from-[#E3000F] to-[#0052CC] text-white text-xs font-bold px-3 py-1.5 rounded-full uppercase tracking-widest hover:opacity-90 transition animate-pulse" data-testid="wheel-header-link">
              🎡 Roue de la chance
            </Link>
          </nav>
          <div className="ml-auto hidden lg:flex items-center gap-1 text-sm">
            <span className="text-[#E3000F] font-bold">{t("saleBanner")}</span>
          </div>
        </div>
        {mobileOpen && (
          <div className="je-container pb-3 grid grid-cols-2 gap-1" data-testid="mobile-menu">
            {CATEGORY_LINKS.map((c) => (
              <Link key={c.slug} to={`/products?category=${c.slug}`} onClick={() => setMobileOpen(false)} className="px-3 py-2 text-sm rounded hover:bg-gray-100">{c.name}</Link>
            ))}
            <Link to="/wheel" onClick={() => setMobileOpen(false)} className="px-3 py-2 text-sm rounded bg-gradient-to-r from-[#E3000F] to-[#0052CC] text-white font-bold">🎡 Roue de la chance</Link>
            <Link to="/favorites" onClick={() => setMobileOpen(false)} className="px-3 py-2 text-sm rounded hover:bg-gray-100">Favoris ({fav.count})</Link>
            <Link to="/compare" onClick={() => setMobileOpen(false)} className="px-3 py-2 text-sm rounded hover:bg-gray-100">Comparer ({cmp.count})</Link>
            <Link to="/track" onClick={() => setMobileOpen(false)} className="px-3 py-2 text-sm rounded hover:bg-gray-100">Suivi</Link>
            <Link to="/help" onClick={() => setMobileOpen(false)} className="px-3 py-2 text-sm rounded hover:bg-gray-100">Aide</Link>
            <div className="col-span-2 flex items-center justify-between gap-2 px-3 py-2 border-t mt-1">
              <span className="text-xs text-gray-500 font-semibold uppercase tracking-wide">Devise</span>
              <div className="inline-flex bg-gray-100 rounded-full p-0.5" data-testid="mobile-currency-switch">
                {CURRENCIES.map((c) => (
                  <button
                    key={c.code}
                    onClick={() => setCurrency(c.code)}
                    className={`px-3 py-1 text-xs font-bold rounded-full transition ${currency === c.code ? "bg-[#0052CC] text-white shadow" : "text-gray-600"}`}
                    data-testid={`mobile-currency-${c.code}`}
                  >
                    {c.symbol} {c.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}

function NotifBell() {
  const [count, setCount] = useState(0);
  useEffect(() => {
    const load = () => api.get("/notifications").then(({ data }) => setCount(data.unread || 0)).catch(() => {});
    load();
    const id = setInterval(load, 60_000);
    return () => clearInterval(id);
  }, []);
  return (
    <Link to="/account?tab=notifications" className="relative flex items-center text-gray-700 hover:text-[#0052CC] transition" data-testid="notif-bell" aria-label="Notifications">
      <Bell className="w-6 h-6" />
      {count > 0 && (
        <span className="absolute -top-1.5 -right-1.5 bg-[#E3000F] text-white text-[10px] font-bold rounded-full min-w-[16px] h-[16px] px-1 flex items-center justify-center" data-testid="notif-count">
          {count > 9 ? "9+" : count}
        </span>
      )}
    </Link>
  );
}

